<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import * as Sentry from "@sentry/vue";
import { PokerRunEvent } from "@models/PokerRunEvent";
import { Item } from "@models/Item";
import Constants from "@/models/Constants";
import { System } from "@models/System";
import EventCost from "@/components/EventCost.vue";
import EventHeader from "@/components/EventHeader.vue";
import Payment from "@/components/Payment.vue";
import Notification from "@/components/Notification.vue";
import Tooltip from "@/components/Tooltip.vue";
import CustomItemSelector from "@/components/CustomItemSelector.vue";
import PokerRunEventDataService from "@/services/PokerRunEventDataService";
import UserTransactionDataService from "@/services/UserTransactionDataService";
import { useUserProfileStore } from "@/store/UserProfile";
import { UserTransaction } from "@models/UserTransaction";
import useCheckout from "@/modules/useCheckout";
import useDate from "@/modules/useDate";
import MobileLinks from "@/components/MobileLinks.vue";
import ConnectedAccountDataService from "@/services/ConnectedAccountDataService";
import AppButton from "@/components/AppButton.vue";
import AppCancel from "@/components/AppCancel.vue";

const { formatISODate } = useDate();

const route = useRoute();
const router = useRouter();
const userProfileStore = useUserProfileStore();
const eventId = ref("");
const pokerRun = ref<PokerRunEvent>();
const eventCode = ref<string>();
const enteredCode = ref<string>();
const items = ref<Item[]>([]);
const lineItems = ref<Item[]>([]);
const systemSettings = ref<System>({} as System);

const refpayment = ref(null);
const includeExtraCard = ref(false);
const today = new Date();
today.setHours(0, 0, 0, 0);

const {
  stepDetails,
  stepSummary,
  stepPayment,
  transactionId,
  currentStep,
  isBusy,
  paymentIntentId,
  clientSecret,
  transactionCharge,
  message,
  messageDetails,
  messageType,
  defaultConnectedAccount,
  continueCheckout,
  cancelCheckout,
  paymentSuccess,
  paymentError,
  paymentInitalized,
} = useCheckout(
  "registration",
  route.params.id as string,
  refpayment,
  () => lineItems.value,
  () => {
    if (currentStep.value === stepDetails) {
      return stepSummary;
    } else if (
      currentStep.value === stepSummary &&
      transactionCharge.value.subtotal === 0
    ) {
      return stepDetails;
    } else if (currentStep.value === stepSummary) {
      return stepPayment;
    } else {
      return stepDetails;
    }
  },
  route.query.fundraiser as string
);

const lineItemsWithServiceFee = computed<Item[]>(() => {
  if (!pokerRun.value) return [];
  const fees: Item[] = [...lineItems.value];

  if (
    !transactionCharge.value.isServiceFeeIncluded &&
    transactionCharge.value.amount > 0
  ) {
    fees.push(({
      name: Constants.SERVICE_FEE,
      amount: transactionCharge.value.serviceFee,
      itemId: Constants.SERVICE_FEE,
      itemType: Constants.SERVICE_FEE,
    } as unknown) as Item);
  }
  return fees;
});

const isZeroDollarEvent = computed(() => {
  if (!pokerRun.value) return false;
  return pokerRun.value.lineItems.reduce((prev, curr) => prev + curr.amount, 0) === 0;
});

const maxCapacity = computed(() => {
  return pokerRun.value && pokerRun.value?.eventSettings.registrationCap
    ? (pokerRun.value?._internal?.registrationCount ?? 0) >=
    (pokerRun.value?.eventSettings.registrationCap ?? 0)
    : false;
});

const connectedAccount = computed(() => {
  if(pokerRun.value?._internal.connectedAccountDetails?.stripeType === 'legacy') {
    return null;
  }

  return pokerRun.value?._internal.connectedAccountDetails?.onboarded ?
    pokerRun.value?._internal?.connectedAccountDetails.id :
    defaultConnectedAccount.value;
});

const canRegister = computed(
  () =>
    pokerRun.value?.status === "active" &&
    pokerRun.value?.eventSettings.registrationStartDate.substring(0, 10) <=
    formatISODate(new Date()) &&
    pokerRun.value?.eventDate.substring(0, 10) >= formatISODate(today) &&
    !maxCapacity.value &&
    pokerRun.value?.eventSettings.registrationStatus !== "closed" &&
    !transactionCharge.value.exceedsPreSaleLimit
);

const calculateFinalLineItems = () => {
  const finalItems: Item[] = [];

  // Add all items based on their quantities
  items.value.forEach(item => {
    if (item.quantity > 0) {
      for (let i = 0; i < item.quantity; i++) {
        finalItems.push({ ...item });
      }
    }
  });

  // Add extra cards if enabled
  if (includeExtraCard.value) {
    const registrationCount = items.value
      .filter(item => item.itemType === Constants.FEE_TYPE)
      .reduce((sum, item) => sum + (item.quantity || 0), 0);

    const extraCard = pokerRun.value?.lineItems.find(
      x => x.itemType === Constants.EXTRA_CARD_TYPE
    );

    if (extraCard) {
      for (let i = 0; i < registrationCount; i++) {
        finalItems.push({ ...extraCard });
      }
    }
  }

  lineItems.value = finalItems;
};

watch([() => items.value, includeExtraCard], () => {
  calculateFinalLineItems();
}, { deep: true });

const initialize = async () => {
  UserTransactionDataService.warmup();
  ConnectedAccountDataService.warmup();
  const pokerRunEvent: PokerRunEvent = await PokerRunEventDataService.get(
    route.params.id as string
  );

  pokerRun.value = pokerRunEvent;

  // Initialize all items with quantity 0
  items.value = pokerRunEvent.lineItems.map(item => ({
    ...item,
    quantity: 0
  }));

  Sentry.setContext("event", {
    eventId: pokerRun.value.id,
    eventName: pokerRun.value.name,
  });

  const transactions: UserTransaction[] = await UserTransactionDataService.getUserTransactions(
    userProfileStore.profile.id,
    route.params.id as string
  );
};

initialize();
</script>

<template>
  <AppEventContainer :event="pokerRun">
    <div class="container">
      <div class="mt-5 text-center w-full bg-white m-2 hidden md:block">
        <div>
          <p class="text-xl m-3">Get the mobile app to register for events.</p>
          <MobileLinks :show-android="true" :show-ios="true"></MobileLinks>
        </div>
      </div>
      <div class="md:hidden">
        <div v-if="
          pokerRun?.eventSettings.isPrivateEvent &&
          eventCode?.toLocaleLowerCase() !==
          pokerRun?.eventSettings.registrationCode?.toLocaleLowerCase()
        ">
          <div class="mb-4">
            <label class="block text-normal-emphasis font-medium mb-2">Please enter the event registration code.
              <div class="flex flex-col items-center justify-center">
                <input class="w-full border border-gray-200 rounded p-2" type="text" v-model="enteredCode" />
              </div>
            </label>
            <p v-show="eventCode &&
              eventCode.toLocaleLowerCase() !==
              pokerRun?.eventSettings.registrationCode.toLocaleLowerCase()
              " class="text-critical text-sm mt-1">
              Invalid registration code.
            </p>
          </div>
          <div class="pb-4 flex flex-col items-center">
            <AppButton
              :disabled="!canRegister"
              :is-loading="isBusy"
              title="Continue"
              buttonType="Primary"
              @click="() => (eventCode = enteredCode)"
            />
            <AppCancel @click="cancelCheckout" />
          </div>
        </div>
        <div v-else>
          <div v-show="currentStep === stepDetails">
              <CustomItemSelector v-model="items" :poker-run="pokerRun" :include-extra-card="includeExtraCard"
                :enable-extra-card="pokerRun?.eventSettings.enableExtraCard ?? false"
                @update:include-extra-card="val => includeExtraCard = val" />
          </div>

          <EventCost v-show="currentStep === stepSummary" class="pb-2" :line-items="lineItemsWithServiceFee"
            :total-amount="transactionCharge.subtotal"></EventCost>

          <div class="py-2 flex flex-col items-center" v-show="currentStep === stepPayment"
            v-if="clientSecret">
            <Payment ref="refpayment" :client-secret="clientSecret" :line-items="lineItems"
              :connected-account-id="connectedAccount" @error="paymentError" @success="paymentSuccess"
              @initialized="paymentInitalized"></Payment>
          </div>

          <div class="pb-4 flex flex-col items-center">
            <div class="text-xl font-semibold" v-if="pokerRun?.eventSettings.registrationStatus === 'closed'">
              The event registration is closed.
            </div>
            <div class="text-xl font-semibold" v-else-if="maxCapacity">
              The event is full and is no longer accepting registrations.
            </div>
            <div class="text-xl font-semibold"
              v-else-if="transactionCharge.exceedsPreSaleLimit">
              This event is unable to accept registrations at this time. Please try again later.
            </div>
            <AppButton
              :disabled="!canRegister"
              :is-loading="isBusy"
              :title="currentStep === stepDetails ? 'Continue' : 'Check Out'"
              buttonType="Primary"
              @click="continueCheckout"
            />
            <AppCancel @click="cancelCheckout">
              {{ currentStep === stepDetails ? "Cancel" : "Edit Order" }}
            </AppCancel>
          </div>
          <div class="text-center">
            *If purchasing more than one registration, you can assign them to other people
            after check out.
          </div>
        </div>
      </div>
    </div>
  </AppEventContainer>
</template>